import React from 'react';
import { RateType } from 'lib/enums';
import { roundUp } from 'lib/pricing';
import {
  ESnapshotExists,
  ERate,
  EOrganization,
  EDisplayParams,
  ESnapshot
} from 'lib/types';
import {
  getCurrencySymbol,
  getDisplayUnits,
  getNoticePreviewLines
} from 'lib/helpers';
import { Badge } from 'lib/components/Badge';
import { selectColumns, selectNoticeType } from 'redux/placement';
import { useAppSelector } from 'redux/hooks';
import { shouldHidePricePreview } from './helpers/pricePreview';

type EstimatedPriceContainerProps = {
  newspaper: ESnapshotExists<EOrganization> | undefined;
  rate: ESnapshot<ERate> | undefined;
  price: string;
};

export function EstimatedPriceContainer({
  newspaper,
  rate,
  price
}: EstimatedPriceContainerProps) {
  const currencySymbol = getCurrencySymbol(newspaper?.data().currency);

  const hidePricePreview = shouldHidePricePreview({ newspaper, rate });

  if (hidePricePreview) {
    return null;
  }

  return (
    <div className="flex flex-row justify-between text-md mb-4">
      <div>{'Estimated Price: '}</div>
      <div className="text-column-green-600">{`${currencySymbol}${
        Number(price) ? Number(price).toFixed(2) : '--'
      }`}</div>
    </div>
  );
}

type DisplayParamsContainerProps = {
  displayParams: EDisplayParams | null;
  rate: ESnapshot<ERate> | undefined;
};

export function DisplayParamsContainer({
  displayParams,
  rate
}: DisplayParamsContainerProps) {
  const measurement = getDisplayUnits(rate, displayParams);

  const noticeType = useAppSelector(selectNoticeType);
  const lines = getNoticePreviewLines(rate, displayParams, noticeType);

  return (
    <div className="w-full">
      <div className="flex justify-between overflow-auto">
        <Badge shape="rectangle">
          {`${RateType.word_count.plural}: ${
            displayParams && displayParams.words ? displayParams.words : 0
          }`}
        </Badge>
        <Badge shape="rectangle">{`${RateType.line.plural}: ${lines}`}</Badge>
        <Badge shape="rectangle">
          {`${measurement.unit}: ${
            measurement.value
              ? roundUp(measurement.value, rate?.data()?.roundOff).toFixed(2)
              : 0
          }`}
        </Badge>
      </div>
    </div>
  );
}
type PreviewPlaceholderProps = {
  displayParams: EDisplayParams | null;
};
export function PreviewPlaceholder({ displayParams }: PreviewPlaceholderProps) {
  const placementColumns = useAppSelector(selectColumns);
  const NUM_COLS = displayParams?.columns || placementColumns || 1;
  const columns = new Array(NUM_COLS).fill(0);

  return (
    <div
      id="preview-placeholder"
      className="p-3 text-2xl flex h-full w-full overflow-hidden"
    >
      <div
        className={`bg-white h-full w-full grid grid-cols-${NUM_COLS} gap-y-2 gap-x-2`}
      >
        {columns.map((_, index) => (
          <div key={`gradient-${index}`} className="gradient" />
        ))}
      </div>
      <style>{`
      .gradient {
        background-image: linear-gradient(0deg, #e9ecef 12.50%, #ffffff 12.50%, #ffffff 50%, #e9ecef 50%, #e9ecef 62.50%, #ffffff 62.50%, #ffffff 100%);
        background-size: 24.00px 24.00px;
      }`}</style>
    </div>
  );
}
